import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  forwardRef,
  input,
  signal
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormsModule
} from '@angular/forms';
import {
  ToggleSwitch,
  ToggleSwitchChangeEvent,
  ToggleSwitchStyle
} from 'primeng/toggleswitch';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'm-switch',
  templateUrl: './m-switch.component.html',
  styleUrls: ['./m-switch.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MSwitchComponent),
      multi: true
    },
    ToggleSwitchStyle
  ],
  imports: [ToggleSwitch, FormsModule]
})
export class MSwitchComponent
  extends ToggleSwitch
  implements ControlValueAccessor
{
  isToggle = input(false); // Styling only: adds the `toggle` CSS variant. Not the checked state. (Distinct from `isChecked` = on/off state and `onToggle` = change callback.)
  switchSize = input<'sm' | 'lg'>('lg');
  onToggle = input<((event: ToggleSwitchChangeEvent) => void) | null>(null);
  isChecked = input<boolean | null>(null);

  // Drives checked state when rendered dynamically (e.g. m-table cells) where
  // ngModel/formControl bindings are unavailable.
  private readonly syncCheckedState = effect(() => {
    const checked = this.isChecked();
    if (checked !== null) {
      this.modelValue.set(checked);
    }
  });

  switchClasses = computed(() => {
    const classes: string[] = [];

    if (this.isToggle()) {
      classes.push('toggle');
    }

    if (this.switchSize() === 'sm') {
      classes.push('small');
    }

    if (this.styleClass) {
      classes.push(this.styleClass);
    }

    return classes.join(' ');
  });

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private _onChange: (value: unknown) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private _onTouched: () => void = () => {};

  // Local disabled state driven by the CVA (PrimeNG 20 `disabled` is a read-only signal input)
  protected disabledState = signal(false);

  // PrimeNG 20 applies root host class/style + a host click on the host element via
  // `host: { '[class]': "cn(cx('root'), styleClass)", '[style]': "sx('root')", '(click)': 'onHostClick()' }`.
  // This wrapper extends ToggleSwitch but renders its OWN inner <p-toggleswitch>, so that metadata is
  // inherited onto <m-switch> too — duplicating the switch styling AND double-toggling on click
  // (the inherited onHostClick fights the inner switch via ngModel, so the toggle appears dead).
  // Neutralize on the wrapper host; the inner instance is unaffected.
  override cx(): string {
    return '';
  }

  override sx(): { [klass: string]: unknown } | undefined {
    return undefined;
  }

  override onHostClick(): void {
    // no-op: the inner <p-toggleswitch> handles its own click
  }

  get value(): unknown {
    return this.modelValue();
  }

  set value(val: unknown) {
    this.modelValue.set(val);
  }

  override writeValue(value: unknown): void {
    this.modelValue.set(value);
  }

  override registerOnChange(fn: (value: unknown) => void): void {
    this._onChange = fn;
  }

  override registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  override setDisabledState(isDisabled: boolean): void {
    this.disabledState.set(isDisabled);
  }

  handleValueChange(newValue: unknown): void {
    this.modelValue.set(newValue);
    this._onChange(newValue);
    this._onTouched();
  }
}
